import { Component, ErrorInfo, ReactNode } from 'react';
import { captureException } from '@/lib/sentry-defer';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class RouteErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error);
    if (import.meta.env.DEV) {
      console.error('Route error:', error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      const errorMsg = this.state.error?.message ?? 'Unknown error';
      const coachPrefill = `Something broke on a page I was using. Error: ${errorMsg.slice(0, 200)}`;

      return (
        <div
          className="min-h-dvh flex items-center justify-center px-6 bg-background dark:bg-black"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="w-full max-w-sm flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-[72px] h-[72px] rounded-full flex items-center justify-center bg-foreground/5 dark:bg-white/10">
                <span className="font-black text-2xl tracking-tight text-foreground">J</span>
              </div>
              <span
                className="absolute bottom-0 right-0 w-[10px] h-[10px] rounded-full bg-green-500 border-2 border-background dark:border-black"
                aria-hidden
              />
            </div>

            <h1 className="font-black text-3xl tracking-tight text-foreground mt-8">
              Something broke.
            </h1>
            <p className="text-sm font-medium text-muted-foreground max-w-[280px] mt-3">
              Not your fault. Try reloading, or tell Jake what you were doing.
            </p>

            {import.meta.env.DEV && (
              <div className="w-full mt-4 pt-4 border-t border-border/50">
                <p className="text-xs font-mono text-muted-foreground break-all text-left">
                  {errorMsg}
                </p>
              </div>
            )}

            <div className="w-full flex flex-col items-center mt-10 gap-4">
              <button
                onClick={this.handleRetry}
                className="w-full py-3.5 rounded-2xl text-sm font-black active:scale-[0.98] transition-transform"
                style={{ background: 'hsl(var(--foreground))', color: 'hsl(var(--background))' }}
              >
                Reload page
              </button>
              <Link
                to={`/coach?prefill=${encodeURIComponent(coachPrefill)}`}
                className="text-sm font-bold text-muted-foreground active:opacity-70"
              >
                Or tell Jake
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
